/**
 * Claim Company Modal Component
 * Allows authenticated users to submit ownership claim requests for companies
 */

import React, { useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { motion } from 'framer-motion';
import {
  BuildingOfficeIcon,
  XMarkIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { apiClient } from '../services/api';
import toast from 'react-hot-toast';

interface ClaimCompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  companyName: string;
  onSuccess?: () => void;
}

interface ClaimResponse {
  success: boolean;
  message: string;
  ownership_id: string;
  status: string;
}

const ClaimCompanyModal: React.FC<ClaimCompanyModalProps> = ({
  isOpen,
  onClose,
  companyId,
  companyName,
  onSuccess,
}) => {
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const MAX_NOTES_LENGTH = 500;

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      // Get JWT token from localStorage
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('กรุณาเข้าสู่ระบบก่อนทำการ claim');
      }

      // Make API request
      const response = await apiClient.post<ClaimResponse>(
        '/api/ownership/claim',
        {
          company_id: companyId,
          notes: notes.trim() || undefined,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Success
      toast.success('ส่งคำขอ claim บริษัทเรียบร้อย รอผู้ดูแลระบบอนุมัติ', {
        duration: 5000,
        icon: '✅',
      });

      // Reset form
      setNotes('');

      // Close modal
      onClose();

      // Call success callback
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Failed to claim company:', err);

      // Extract error message
      const errorMessage =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        'เกิดข้อผิดพลาดในการส่งคำขอ';

      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle close
  const handleClose = () => {
    if (!isSubmitting) {
      setNotes('');
      setError(null);
      onClose();
    }
  };

  // Character count
  const remainingChars = MAX_NOTES_LENGTH - notes.length;
  const isOverLimit = remainingChars < 0;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>

        {/* Modal Container */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all">
                {/* Modal Header */}
                <div className="flex items-center justify-between border-b border-neutral-200 px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100">
                      <BuildingOfficeIcon className="h-5 w-5 text-primary-600" />
                    </div>
                    <Dialog.Title className="text-lg font-semibold text-neutral-900">
                      Claim บริษัท
                    </Dialog.Title>
                  </div>
                  <button
                    onClick={handleClose}
                    disabled={isSubmitting}
                    className="rounded-lg p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Modal Body */}
                <form onSubmit={handleSubmit}>
                  <div className="px-6 py-4 space-y-4">
                    {/* Company Name Display */}
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        ชื่อบริษัท
                      </label>
                      <div className="flex items-center space-x-2 p-3 bg-neutral-50 border border-neutral-200 rounded-lg">
                        <BuildingOfficeIcon className="h-5 w-5 text-neutral-500 flex-shrink-0" />
                        <span className="text-neutral-900 font-medium">{companyName}</span>
                      </div>
                    </div>

                    {/* Notes Textarea */}
                    <div>
                      <label htmlFor="notes" className="block text-sm font-medium text-neutral-700 mb-2">
                        เหตุผล/หมายเหตุ <span className="text-neutral-500 font-normal">(ไม่บังคับ)</span>
                      </label>
                      <textarea
                        id="notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        disabled={isSubmitting}
                        placeholder="กรุณาระบุเหตุผลที่ต้องการเป็นเจ้าของบริษัทนี้ (ถ้ามี)"
                        className={`w-full px-4 py-3 border rounded-lg resize-none focus:outline-none focus:ring-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                          isOverLimit
                            ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                            : 'border-neutral-300 focus:border-primary-500 focus:ring-primary-200'
                        }`}
                        rows={4}
                      />
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-xs text-neutral-500">
                          ตัวอย่าง: ฉันเป็นเจ้าของบริษัทนี้และต้องการจัดการข้อมูล
                        </p>
                        <span
                          className={`text-xs font-medium ${
                            isOverLimit
                              ? 'text-red-600'
                              : remainingChars < 50
                              ? 'text-amber-600'
                              : 'text-neutral-500'
                          }`}
                        >
                          {remainingChars}/{MAX_NOTES_LENGTH}
                        </span>
                      </div>
                    </div>

                    {/* Info Box */}
                    <div className="flex items-start space-x-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <ExclamationCircleIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-blue-900">
                        <p className="font-medium mb-1">ข้อมูลสำคัญ</p>
                        <p className="text-blue-700">
                          คำขอของคุณจะถูกส่งไปยังผู้ดูแลระบบเพื่อตรวจสอบและอนุมัติ
                          หลังจากได้รับการอนุมัติ คุณจะสามารถดูและแก้ไขข้อมูลบริษัทได้
                        </p>
                      </div>
                    </div>

                    {/* Error Message */}
                    {error && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-start space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg"
                      >
                        <ExclamationCircleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-700">{error}</p>
                      </motion.div>
                    )}
                  </div>

                  {/* Modal Footer */}
                  <div className="flex items-center justify-end space-x-3 border-t border-neutral-200 px-6 py-4 bg-neutral-50">
                    <button
                      type="button"
                      onClick={handleClose}
                      disabled={isSubmitting}
                      className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50 hover:border-neutral-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ยกเลิก
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting || isOverLimit}
                      className="px-6 py-2 text-sm font-medium text-white bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center space-x-2">
                          <svg
                            className="animate-spin h-4 w-4"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          <span>กำลังส่งคำขอ...</span>
                        </span>
                      ) : (
                        'ส่งคำขอ'
                      )}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ClaimCompanyModal;